import React, { useEffect } from "react";
import { validateProps } from "../../../../shared/props";
import { warnDeprecation } from "../../../../shared/warnDeprecation";
import { useViewport } from "../../../../styles";
import { SlotStack, SlotStackItem } from "../../../Stacks/SlotStack/SlotStack";
import { Stack } from "../../../Stacks/Stack/Stack";
import { ListItemFinalColumn } from "../blocks/ListItemFinalColumn/ListItemFinalColumn";
import { OneColumnListItem } from "../OneColumnListItem/OneColumnListItem";
import { ListItemCanvas, ListItemCanvasProps } from "../shared/ListItemCanvas";
import {
    MultiColumnListItemSharedProps, validCenterColumnComponents, validFinalColumnActionComponents, validFirstColumnComponents
} from "../shared/MultiColumnListItem.types";
import { listItemPadding, multiColumnTotalSlots } from "../shared/styles";
import { listItemMediaMerge, validateNumberOfFinalColumnActions } from "../shared/utils";
import { validateListItemColumnComponent } from "../shared/utils";

export type FourColumnListItemVariant = "ThreeThreeThreeThree" | "SixTwoTwoTwo";
interface FourColumnListItemBaseProps {
    /**
     * The column layout type.
     */
    readonly variant: FourColumnListItemVariant;
}

export type FourColumnListItemProps = FourColumnListItemBaseProps & MultiColumnListItemSharedProps;

interface FourColumnListItemInternalBaseProps extends ListItemCanvasProps {
    readonly firstColumnWidth: number;
    readonly secondColumnWidth: number;
    readonly thirdColumnWidth: number;
    readonly finalColumnWidth: number;
}

type FourColumnListItemInternalProps = MultiColumnListItemSharedProps & FourColumnListItemInternalBaseProps;

/** @deprecated Please use the new ListItem/MobileListItem with ListItemColumns instead. */
export const FourColumnListItem: React.FC<FourColumnListItemProps> = ({
    variant,
    ...listItemProps
}) => {
    switch (variant) {
        case "ThreeThreeThreeThree":
            return (
                <FourColumnListItemInternal
                    firstColumnWidth={3}
                    secondColumnWidth={3}
                    thirdColumnWidth={3}
                    finalColumnWidth={3}
                    {...listItemProps}
                />
            );
        case "SixTwoTwoTwo":
            return (
                <FourColumnListItemInternal
                    firstColumnWidth={6}
                    secondColumnWidth={2}
                    thirdColumnWidth={2}
                    finalColumnWidth={2}
                    {...listItemProps}
                />
            );
    }
};

const FourColumnListItemInternal: React.FC<FourColumnListItemInternalProps> = ({
    firstColumn = [],
    firstColumnWidth,
    secondColumn = [],
    secondColumnWidth,
    thirdColumn = [],
    thirdColumnWidth,
    finalColumnText = [],
    finalColumnActions = [],
    finalColumnWidth,
    finalColumnAlign = "end",
    disabled = false,
    menuDisabled = false,
    ...otherProps
}) => {
    useEffect(() => {
        warnDeprecation("FourColumnListItem has been deprecated, please use the new ListItem/MobileListItem with ListItemColumns instead.");
    }, []);

    validateProps("FourColumnListItemInternal", otherProps);

    //Ensures we only run the validation once at first, and then only for each
    //Column that changes after the first render
    useEffect(() => {
        firstColumn.forEach((firstColumnComponent) => (
            validateListItemColumnComponent(firstColumnComponent, validFirstColumnComponents)
        ));
    }, [firstColumn]);

    useEffect(() => {
        if (secondColumn) {
            secondColumn.forEach((secondColumnComponent) => {
                validateListItemColumnComponent(secondColumnComponent, validCenterColumnComponents);
            });
        }
    }, [secondColumn]);

    useEffect(() => {
        if (thirdColumn) {
            thirdColumn.forEach((thirdColumnComponent) => {
                validateListItemColumnComponent(thirdColumnComponent, validCenterColumnComponents);
            });
        }
    }, [thirdColumn]);

    useEffect(() => {
        if (finalColumnText) {
            finalColumnText.forEach((finalColumnTextComponent) => (
                validateListItemColumnComponent(finalColumnTextComponent, validCenterColumnComponents)
            ));
        }
    }, [finalColumnText]);

    useEffect(() => {
        if (finalColumnActions) {
            finalColumnActions.forEach((finalColumnActionsComponent) => (
                validateListItemColumnComponent(finalColumnActionsComponent, validFinalColumnActionComponents)
            ));
        }
    }, [finalColumnActions]);

    const viewportType = useViewport();

    const isClickableListItem = otherProps.onClickProps !== undefined;
    validateNumberOfFinalColumnActions("FourColumn", isClickableListItem, finalColumnActions.length);

    if (viewportType === "s" || viewportType === "m" || viewportType === "l") {
        return (
            <OneColumnListItem
                firstColumn={listItemMediaMerge([...firstColumn, ...secondColumn, ...thirdColumn, ...finalColumnText])}
                actions={finalColumnActions as JSX.Element[]}
                actionsAlign="end"
                disabled={disabled}
                menuDisabled={menuDisabled}
                {...otherProps}
            />
        );
    }

    let existingMenuProps;
    if (otherProps.decorationType === "menu") {
        existingMenuProps = { ...otherProps.menuProps };
    }

    return (
        <ListItemCanvas {...otherProps} >
            <SlotStack totalSlots={multiColumnTotalSlots} verticalAlign="center">
                <SlotStackItem slots={firstColumnWidth}>
                    <Stack padding={listItemPadding}>
                        {firstColumn.map((item, index) => React.cloneElement(item, { disabled, key: item.key || index }))}
                    </Stack>
                </SlotStackItem>
                <SlotStackItem slots={secondColumnWidth}>
                    <Stack horizontal={true} childrenGap={4}>
                        {secondColumn.map((item, index) => React.cloneElement(item, { disabled, key: item.key || index }))}
                    </Stack>
                </SlotStackItem>
                <SlotStackItem slots={thirdColumnWidth}>
                    <Stack horizontal={true} childrenGap={4}>
                        {thirdColumn.map((item, index) => React.cloneElement(item, { disabled, key: item.key || index }))}
                    </Stack>
                </SlotStackItem>
                <SlotStackItem slots={finalColumnWidth}>
                    <ListItemFinalColumn
                        finalColumnText={finalColumnText}
                        finalColumnActions={finalColumnActions}
                        finalColumnAlign={finalColumnAlign}
                        existingMenuProps={existingMenuProps}
                        collapseActionText={isClickableListItem}
                        iconName={otherProps.decorationType === "icon" ? otherProps.iconName : undefined}
                        disabled={disabled}
                        menuDisabled={menuDisabled}
                    />
                </SlotStackItem>
            </SlotStack>
        </ListItemCanvas>
    );
};
